import React from 'react'

import {
  AlignSelf,
  Body3,
  Box,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { BasketUpsellProductTile } from './BasketUpsellProductTile'
import { Carousel } from './Carousel'
import type {
  BasketUpsellCarouselContent,
  BasketUpsellProduct,
  BasketUpsellProductActionSource,
} from './types'

type BasketUpsellSectionProps = {
  carousel: BasketUpsellCarouselContent
  products: BasketUpsellProduct[]
  onClickProduct: (product: BasketUpsellProduct) => void
  onAddProduct: (productId: string, source: BasketUpsellProductActionSource) => void
  onRemoveProduct: (productId: string, source: BasketUpsellProductActionSource) => void
}

const AnimatedSection = withCitrusTheme(Box, () => ({
  animation: 'basketUpsellFadeIn 180ms ease-out',
  '@keyframes basketUpsellFadeIn': {
    from: {
      opacity: 0,
      transform: 'translateY(6px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}))

export const BasketUpsellSection = ({
  carousel,
  onAddProduct,
  products,
  onClickProduct,
  onRemoveProduct,
}: BasketUpsellSectionProps) => {
  if (products.length < 2) return null

  return (
    <AnimatedSection
      data-testid="basket-upsell-section"
      bg={Color.ColdGrey_800}
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      gap={3}
      paddingV={3}
      style={{ borderRadius: '4px' }}
    >
      <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={2}>
        {carousel.tag && (
          <Box
            data-testid="basket-upsell-carousel-tag"
            alignSelf={AlignSelf.Start}
            paddingH={2}
            paddingV={1}
            style={{
              background: carousel.tag.backgroundColor.light,
              borderRadius: '0 4px 4px 0',
            }}
          >
            <Text color={Color.White} size={1} style={{ color: carousel.tag.color.light }}>
              {carousel.tag.text}
            </Text>
          </Box>
        )}
        <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={2} paddingH={3}>
          <Text color={Color.White} fontWeight={FontWeight.Bold} size={7}>
            {carousel.title}
          </Text>
          <Body3 color={Color.White}>{carousel.subtitle}</Body3>
        </Box>
      </Box>
      <Box paddingH={3}>
        <Carousel
          carouselAreaLabel={carousel.title}
          options={{ align: 'start', loop: false, slidesToScroll: 'auto' }}
          slideWidth="220px"
        >
          {products.map((product) => (
            <BasketUpsellProductTile
              key={product.id}
              product={product}
              onAddProduct={onAddProduct}
              onClickProduct={onClickProduct}
              onRemoveProduct={onRemoveProduct}
            />
          ))}
        </Carousel>
      </Box>
    </AnimatedSection>
  )
}
